<template>
  <div class="adoc-product-example">
    <div class="adoc-product-example__head">
      <div class="adoc-product-example__title">
        <v-icon icon="mdi-shape-outline" size="16" />
        <span>Live example for your products</span>
      </div>
      <v-select
        v-model="selectedContextName"
        :items="contextOptions"
        item-title="label"
        item-value="value"
        placeholder="Select a product"
        variant="solo"
        density="comfortable"
        hide-details="auto"
        flat
        :loading="loading"
        class="adoc-product-example__select"
      />
    </div>

    <p v-if="!loading && !contexts.length" class="adoc-product-example__empty">
      No products found for your organization yet.
    </p>

    <template v-else-if="selectedContext">
      <p class="adoc-product-example__hint">
        Fields below are specific to <strong>{{ selectedContext.context_name }}</strong> --
        every other product may have a different set. Only fields already
        configured for this product can be submitted.
      </p>

      <div class="adoc-table-card">
        <table class="adoc-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Required</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>context_name</code></td>
              <td>string</td>
              <td><span class="adoc-required adoc-required--yes">yes</span></td>
            </tr>
            <tr>
              <td><code>fields.contact_name</code></td>
              <td>string</td>
              <td><span class="adoc-required adoc-required--yes">yes</span></td>
            </tr>
            <tr>
              <td><code>fields.contact_email</code></td>
              <td>string</td>
              <td><span class="adoc-required adoc-required--yes">yes</span></td>
            </tr>
            <tr>
              <td><code>fields.contact_number</code></td>
              <td>string</td>
              <td><span class="adoc-required adoc-required--yes">yes</span></td>
            </tr>
            <tr>
              <td><code>fields.org_name</code></td>
              <td>string</td>
              <td><span class="adoc-required adoc-required--yes">yes</span></td>
            </tr>
            <tr v-for="field in productFields" :key="field.key">
              <td><code>fields.{{ field.key }}</code></td>
              <td>{{ field.typeLabel }}</td>
              <td><span class="adoc-required adoc-required--no">optional</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="adoc-code-card">
        <div class="adoc-code-card__head">
          <div class="adoc-code-card__title">
            <v-icon icon="mdi-code-tags" size="16" />
            <span>Example request for {{ selectedContext.context_name }}</span>
          </div>
          <button type="button" class="adoc-code-card__copy" @click="copyExample">
            <v-icon size="14">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            Copy
          </button>
        </div>
        <pre class="adoc-code-card__pre"><code>{{ exampleJson }}</code></pre>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { fetchContexts } from '@/api/contextsApi'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import { normalizeContextsList } from '@/utils/contexts'

defineOptions({ name: 'ApiDocsContactUsProductExample' })

const store = useStore()
const contexts = ref([])
const loading = ref(false)
const selectedContextName = ref('')
const copied = ref(false)

const SYSTEM_FIELD_KEYS = new Set([
  'contact_name', 'contact_email', 'contact_number',
  'type_of_business', 'industry', 'message', 'comments',
])

const TYPE_LABELS = {
  TEXT: 'string',
  NUMBER: 'number',
  DROPDOWN: 'string (one of a fixed list)',
  DATE: 'number (epoch ms)',
  PHONE_NUMBER: 'string (e.g. +919876543210)',
  EMAIL: 'string',
}

const EXAMPLE_VALUES = {
  TEXT: 'Sample value',
  NUMBER: 42,
  DATE: Date.now(),
  PHONE_NUMBER: '+919876543210',
  EMAIL: 'name@example.com',
}

const contextOptions = computed(() =>
  contexts.value.map((c) => ({ label: c.context_name, value: c.context_name })),
)

const selectedContext = computed(() =>
  contexts.value.find((c) => c.context_name === selectedContextName.value) || null,
)

const productFields = computed(() => {
  const defs = selectedContext.value?.custom_field_definitions || {}
  return Object.entries(defs)
    .filter(([key, def]) => !SYSTEM_FIELD_KEYS.has(key) && def?.enabled !== false)
    .map(([key, def]) => ({
      key,
      type: def.type || 'TEXT',
      typeLabel: TYPE_LABELS[def.type] || 'string',
      options: def.options || [],
    }))
})

function exampleValueFor(field) {
  if (field.type === 'DROPDOWN') return field.options[0] || 'Option A'
  return EXAMPLE_VALUES[field.type] ?? 'Sample value'
}

const exampleJson = computed(() => {
  if (!selectedContext.value) return ''
  const fieldsMap = {}
  for (const field of productFields.value) {
    fieldsMap[field.key] = exampleValueFor(field)
  }
  const body = {
    api_key: 'YOUR_API_KEY',
    internal_org_id: String(store.state.internal_org_id || 'your-org-id'),
    context_name: selectedContext.value.context_name,
    fields: {
      contact_name: 'Jane Doe',
      contact_email: 'jane@example.com',
      contact_number: '+919876543210',
      org_name: 'Acme Corp',
      ...fieldsMap,
    },
  }
  return JSON.stringify(body, null, 2)
})

async function copyExample() {
  try {
    await navigator.clipboard.writeText(exampleJson.value)
    copied.value = true
    window.setTimeout(() => { copied.value = false }, 2000)
  } catch {
    // Clipboard permission denied -- non-fatal, the example is still visible.
  }
}

onMounted(async () => {
  const orgId = String(store.getters.sessionContext?.org_id || '').trim()
  if (!orgId) return
  loading.value = true
  try {
    const response = await fetchContexts({ org_id: orgId })
    if (isApiSuccess(response)) {
      contexts.value = normalizeContextsList(unwrapApiList(response))
      if (contexts.value.length) selectedContextName.value = contexts.value[0].context_name
    }
  } catch {
    // Best-effort docs enhancement -- static docs below remain usable either way.
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.adoc-product-example {
  margin: 20px 0 28px;
  padding: 16px;
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.04);
}

.adoc-product-example__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.adoc-product-example__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 650;
  font-size: 0.85rem;
  color: #4338ca;
}

.adoc-product-example__select {
  max-width: 260px;
  min-width: 200px;
}

.adoc-product-example__hint {
  font-size: 0.8rem;
  color: #64748b;
  margin: 4px 0 14px;
}

.adoc-product-example__empty {
  font-size: 0.85rem;
  color: #94a3b8;
  margin: 4px 0 0;
}
</style>
